(function initializeCompatibility(windowRef, documentRef) {
  if (windowRef.GTMTrackingCompatibility) return;

  const bootstrapNode = documentRef.getElementById('GtmTrackingBootstrap');
  let config = {};
  try {
    config = JSON.parse(bootstrapNode?.textContent || '{}');
  } catch (error) {
    console.warn('[GTM compatibility] Invalid bootstrap JSON', error);
  }
  const rawGtmId = typeof config.gtmId === 'string' ? config.gtmId.trim() : '';
  const gtmId = /^GTM-[A-Z0-9]+$/.test(rawGtmId) ? rawGtmId : null;

  let initialized = false;
  let pageEventsPushed = false;
  let generation = 0;
  let gtmLoaded = false;
  let cartUnsubscribe = null;
  let cartRefresh = null;
  let cartRefreshRequested = false;
  let cartRehydration = null;
  let cartRehydrationEvents = [];
  let cartReady = true;
  let cartStateVersion = 0;
  let hasInitialized = false;
  let invalidGtmIdWarned = false;
  let lastCheckoutAt = -Infinity;
  const removers = [];

  const money = (value) => Number(value || 0) / 100;
  const asId = (value) => (value == null ? null : String(value).replace(/^gid:\/\/shopify\/\w+\//, ''));
  const productData = (productOrContext) => productOrContext?.data || productOrContext;
  const firstVariant = (productOrContext) =>
    productOrContext?.selectedVariant || productData(productOrContext)?.variants?.[0] || null;
  const gProductId = (productId, variantId) =>
    productId && variantId ? `shopify_${config.feedRegion}_${asId(productId)}_${asId(variantId)}` : null;
  const push = (payload) => windowRef.dataLayer.push(payload);
  const isNonNegativeNumber = (value) => typeof value === 'number' && Number.isFinite(value) && value >= 0;
  const isNonNegativeInteger = (value) => isNonNegativeNumber(value) && Number.isInteger(value);

  function normalizeLine(item) {
    const quantity = item?.quantity;
    if (
      !item ||
      typeof item !== 'object' ||
      item.key == null ||
      item.key === '' ||
      !isNonNegativeInteger(quantity) ||
      !isNonNegativeNumber(item.price) ||
      !isNonNegativeNumber(item.final_price)
    ) {
      return null;
    }
    return {...item, key: String(item.key), quantity};
  }

  function normalizeCart(cart) {
    if (
      !cart ||
      typeof cart !== 'object' ||
      !Array.isArray(cart.items) ||
      !isNonNegativeInteger(cart.item_count) ||
      !isNonNegativeNumber(cart.total_price)
    ) {
      return null;
    }
    const lineMap = new Map();
    for (const rawItem of cart.items) {
      const item = normalizeLine(rawItem);
      if (!item) return null;
      lineMap.set(item.key, item);
    }
    const items = [...lineMap.values()];
    if (items.reduce((total, item) => total + item.quantity, 0) !== cart.item_count) return null;
    return {
      ...cart,
      items,
      lineMap,
    };
  }

  function lineIdentity(item) {
    if (item.variant_id == null) return null;
    const properties = item.properties && typeof item.properties === 'object' ? item.properties : {};
    const sortedProperties = Object.keys(properties)
      .sort()
      .map((key) => [key, properties[key]]);
    const sellingPlanId =
      item.selling_plan_allocation?.selling_plan?.id ??
      item.selling_plan_allocation?.selling_plan_id ??
      item.selling_plan ??
      null;
    return JSON.stringify([asId(item.variant_id), sortedProperties, asId(sellingPlanId)]);
  }

  function equivalentLine(lineMap, item) {
    const identity = lineIdentity(item);
    if (!identity) return null;
    return [...lineMap.values()].find((line) => lineIdentity(line) === identity) || null;
  }

  let cartSnapshot =
    normalizeCart(config.cart) || {items: [], item_count: 0, total_price: 0, lineMap: new Map()};

  function compactUser(user) {
    if (user?.type === 'visitor') return {type: 'visitor'};
    return user || {};
  }

  function buildInfoEvent() {
    return {
      event: 'sh_info',
      page_type: config.pageType,
      page_currency: config.currency,
      user: compactUser(config.user),
    };
  }

  function buildImpressionEvent(collection) {
    const products = collection.products || [];
    return {
      event: 'ee_productImpression',
      category_name: collection.title,
      category_id: collection.id,
      category_handle: collection.handle,
      product_brand: products.map((product) => product.vendor),
      product_type: products.map((product) => product.type),
      product_sku: products.map((product) => firstVariant(product)?.sku),
      product_name: products.map((product) => product.title),
      product_handle: products.map((product) => product.handle),
      product_id: products.map((product) => asId(product.id)),
      product_price: products.map((product) => money(firstVariant(product)?.price)),
      currency: config.currency,
      product_position: products.map((_product, index) => index + 1),
      g_product_id: products.map((product) => gProductId(product.id, firstVariant(product)?.id)),
      variant_id: products.map((product) => asId(firstVariant(product)?.id)),
      variant_title: products.map((product) => firstVariant(product)?.title),
    };
  }

  function searchProducts() {
    return (config.search?.products || []).filter(
      (product) => !product.object_type || product.object_type === 'product',
    );
  }

  function buildSearchEvent(search) {
    const products = searchProducts();
    return {
      event: 'searchListInfo',
      page_type: 'search',
      search_term: search.term,
      search_results: search.resultsCount,
      product_brand: products.map((product) => product.vendor),
      product_type: products.map((product) => product.type),
      product_sku: products.map((product) => firstVariant(product)?.sku),
      product_name: products.map((product) => product.title),
      product_handle: products.map((product) => product.handle),
      product_id: products.map((product) => asId(product.id)),
      product_price: products.map((product) => money(firstVariant(product)?.price)),
      currency: config.currency,
      product_position: products.map((_product, index) => index + 1),
      product_list_id: 'search',
      product_list_name: 'Search',
      g_product_id: products.map((product) => gProductId(product.id, firstVariant(product)?.id)),
      variant_id: products.map((product) => asId(firstVariant(product)?.id)),
      variant_title: products.map((product) => firstVariant(product)?.title),
    };
  }

  function buildDetailEvent(productContext) {
    const product = productData(productContext);
    const variant = firstVariant(productContext);
    if (!product || !variant) return null;
    return {
      event: 'ee_productDetail',
      product_id: asId(product.id),
      product_name: product.title,
      product_handle: product.handle,
      product_type: product.type,
      product_brand: product.vendor,
      product_sku: variant.sku,
      product_price: money(variant.price),
      currency: config.currency,
      category_id: productContext.collection?.id ?? null,
      category_name: productContext.collection?.title ?? null,
      category_handle: productContext.collection?.handle ?? null,
      g_product_id: gProductId(product.id, variant.id),
      variant_id: asId(variant.id),
      variant_title: variant.title,
    };
  }

  function buildClickEvent(product, list) {
    const variant = firstVariant(product);
    if (!variant) return null;
    return {
      event: 'ee_productClick',
      product_id: asId(product.id),
      product_name: product.title,
      product_handle: product.handle,
      product_type: product.type,
      product_sku: variant.sku,
      product_brand: product.vendor,
      product_price: money(variant.price),
      category_id: list.id,
      category_name: list.name,
      category_handle: list.handle,
      currency: config.currency,
      product_position: list.position,
      variant_id: asId(variant.id),
      variant_title: variant.title,
    };
  }

  function buildLineEvent(eventName, item, quantity) {
    return {
      event: eventName,
      product_name: item.product_title,
      product_handle: item.handle,
      product_id: asId(item.product_id),
      product_price: money(item.price),
      currency: item.currency || config.currency,
      product_brand: item.vendor,
      product_type: item.product_type,
      quantity,
      variant_id: asId(item.variant_id),
      variant_title: item.variant_title,
      product_sku: item.sku,
      g_product_id: gProductId(item.product_id, item.variant_id),
    };
  }

  function buildCartEvent(eventName) {
    const items = cartSnapshot.items;
    const event = {
      event: eventName,
      page_type: 'cart',
      product_id: items.map((item) => asId(item.product_id)),
      product_name: items.map((item) => item.product_title),
      product_handle: items.map((item) => item.handle),
      product_type: items.map((item) => item.product_type),
      product_brand: items.map((item) => item.vendor),
      product_sku: items.map((item) => item.sku),
      variant_id: items.map((item) => asId(item.variant_id)),
      variant_title: items.map((item) => item.variant_title),
      product_price: items.map((item) => money(item.price)),
      currency: config.currency,
      quantity: items.map((item) => item.quantity),
      totalValue: money(cartSnapshot.total_price),
      totalQuantity: Number(cartSnapshot.item_count),
      g_product_id: items.map((item) => gProductId(item.product_id, item.variant_id)),
    };
    if (eventName === 'ee_cartView') {
      event.product_list_id = 'cart';
      event.product_list_name = 'Cart';
    }
    return event;
  }

  function diffCarts(cartData) {
    const result = {additions: [], removals: []};
    const isFullCart = Array.isArray(cartData?.items);

    if (!isFullCart) {
      const item = normalizeLine(cartData);
      if (!item) return result;
      const previousItem = cartSnapshot.lineMap.get(item.key) || equivalentLine(cartSnapshot.lineMap, item);
      const quantity = item.quantity - (previousItem?.quantity || 0);
      if (quantity > 0) result.additions.push({item, quantity});
      if (quantity < 0) result.removals.push({item: previousItem, quantity: -quantity});
      const lineMap = new Map(cartSnapshot.lineMap);
      if (previousItem && previousItem.key !== item.key) lineMap.delete(previousItem.key);
      if (item.quantity === 0) lineMap.delete(item.key);
      else lineMap.set(item.key, item);
      const items = [...lineMap.values()];
      cartSnapshot = {
        ...cartSnapshot,
        items,
        item_count: items.reduce((total, line) => total + line.quantity, 0),
        lineMap,
      };
      if (!previousItem || JSON.stringify(previousItem) !== JSON.stringify(item)) cartStateVersion += 1;
      return result;
    }

    const nextSnapshot = normalizeCart(cartData);
    if (!nextSnapshot) return result;
    const previousLines = new Map(cartSnapshot.lineMap);
    const nextLines = new Map(nextSnapshot.lineMap);
    for (const [key, item] of nextSnapshot.lineMap) {
      const previousItem = previousLines.get(key);
      if (!previousItem) continue;
      const quantity = item.quantity - previousItem.quantity;
      if (quantity > 0) result.additions.push({item, quantity});
      if (quantity < 0) result.removals.push({item: previousItem, quantity: -quantity});
      previousLines.delete(key);
      nextLines.delete(key);
    }
    for (const [key, item] of nextLines) {
      const previousItem = equivalentLine(previousLines, item);
      if (!previousItem) continue;
      const quantity = item.quantity - previousItem.quantity;
      if (quantity > 0) result.additions.push({item, quantity});
      if (quantity < 0) result.removals.push({item: previousItem, quantity: -quantity});
      previousLines.delete(previousItem.key);
      nextLines.delete(key);
    }
    for (const item of nextLines.values()) result.additions.push({item, quantity: item.quantity});
    for (const item of previousLines.values()) {
      const quantity = item.quantity;
      if (quantity > 0) result.removals.push({item, quantity});
    }
    cartSnapshot = nextSnapshot;
    cartStateVersion += 1;
    return result;
  }

  function fetchCartSnapshot(activeGeneration) {
    if (typeof windowRef.fetch !== 'function') return Promise.resolve(null);
    const cartUrl = `${windowRef.routes?.cart_url || '/cart'}.js`;
    return windowRef
      .fetch(cartUrl)
      .then((response) => {
        if (response.ok === false) throw new Error(`Cart refresh failed with ${response.status}`);
        return response.json();
      })
      .then((cart) => {
        const snapshot = normalizeCart(cart);
        if (!snapshot || !initialized || generation !== activeGeneration) return null;
        return snapshot;
      })
      .catch((error) => {
        console.warn('[GTM compatibility] Cart refresh failed', error);
        return null;
      });
  }

  function requestCartRefresh(activeGeneration) {
    cartRefreshRequested = true;
    if (cartRefresh) return cartRefresh;

    const pending = (async () => {
      while (initialized && generation === activeGeneration && cartRefreshRequested) {
        cartRefreshRequested = false;
        const requestVersion = cartStateVersion;
        const snapshot = await fetchCartSnapshot(activeGeneration);
        if (!snapshot) {
          if (cartRefreshRequested) continue;
          return false;
        }
        if (generation !== activeGeneration || !initialized) return false;
        if (requestVersion !== cartStateVersion) {
          if (!cartReady) cartRefreshRequested = true;
          continue;
        }
        cartSnapshot = snapshot;
        cartReady = true;
        return true;
      }
      return cartReady;
    })();

    cartRefresh = pending;
    const release = () => {
      if (cartRefresh !== pending) return;
      cartRefresh = null;
      if (cartRefreshRequested && initialized && generation === activeGeneration) requestCartRefresh(activeGeneration);
    };
    pending.then(release, release);
    return pending;
  }

  function handleCartUpdate(event, activeGeneration = generation, deferRefresh = false) {
    const isFullCart = Array.isArray(event?.cartData?.items);
    if (isFullCart && !normalizeCart(event.cartData)) return false;
    if (!isFullCart && !normalizeLine(event?.cartData)) return true;
    const {additions, removals} = diffCarts(event?.cartData);
    additions.forEach(({item, quantity}) => push(buildLineEvent('ee_addToCart', item, quantity)));
    removals.forEach(({item, quantity}) => push(buildLineEvent('ee_removeFromCart', item, quantity)));
    if (isFullCart) {
      cartReady = true;
      cartRefreshRequested = false;
      return true;
    }
    cartReady = false;
    if (deferRefresh) return true;
    return requestCartRefresh(activeGeneration);
  }

  function rehydrateCart(activeGeneration) {
    cartReady = false;
    const pending = requestCartRefresh(activeGeneration);
    cartRehydration = pending;
    const replay = () => {
      if (cartRehydration !== pending || !initialized || generation !== activeGeneration) return false;
      cartRehydration = null;
      const events = cartRehydrationEvents;
      cartRehydrationEvents = [];
      events.forEach((event) => {
        try {
          handleCartUpdate(event, activeGeneration, true);
        } catch (error) {
          console.warn('[GTM compatibility] Cart event failed', error);
        }
      });
      if (events.length > 0 && !cartReady) requestCartRefresh(activeGeneration);
      return true;
    };
    pending.then(replay, replay);
    return pending;
  }

  function pushPageEvent() {
    if (pageEventsPushed) return;
    pageEventsPushed = true;
    push(buildInfoEvent());
    if (config.pageType === 'Collection' && config.collection) push(buildImpressionEvent(config.collection));
    if (config.pageType === 'Search' && config.search) push(buildSearchEvent(config.search));
    if (config.pageType === 'Product' && config.product) {
      const detailEvent = buildDetailEvent(config.product);
      if (detailEvent) push(detailEvent);
    }
    if (config.pageType === 'Cart') push(buildCartEvent('ee_cartView'));
  }

  function productClickMap() {
    const collection = config.pageType === 'Collection' ? config.collection : null;
    const products = collection?.products || (config.pageType === 'Search' ? searchProducts() : []);
    return new Map(
      products.map((product, index) => [
        asId(product.id),
        {
          product,
          list: collection
            ? {
                id: collection.id,
                name: collection.title,
                handle: collection.handle,
                position: index + 1,
              }
            : {id: 'search', name: 'Search', handle: null, position: index + 1},
        },
      ]),
    );
  }

  function initProductClicks() {
    const products = productClickMap();
    const onClick = (event) => {
      if (event.target?.closest?.('button[name="checkout"]')) {
        pushCheckoutEvent();
        return;
      }
      const link = event.target?.closest?.('a[id^="CardLink-"], a[id^="StandardCardNoMediaLink-"]');
      const productId = link?.id?.match(/-(\d+)$/)?.[1];
      const match = products.get(productId);
      if (!match) return;
      const clickEvent = buildClickEvent(match.product, match.list);
      if (clickEvent) push(clickEvent);
    };
    const onSubmit = (event) => {
      if (event.submitter && !event.submitter.matches?.('button[name="checkout"]')) return;
      const button = event.submitter || event.target?.querySelector?.('button[name="checkout"]');
      if (!button || (button.form && button.form !== event.target)) return;
      pushCheckoutEvent();
    };
    documentRef.addEventListener('click', onClick);
    documentRef.addEventListener('submit', onSubmit);
    removers.push(() => documentRef.removeEventListener('click', onClick));
    removers.push(() => documentRef.removeEventListener('submit', onSubmit));
  }

  function pushCheckoutEvent() {
    const activeGeneration = generation;
    const now = Date.now();
    if (now - lastCheckoutAt < 500) return;
    lastCheckoutAt = now;
    const trackCheckout = () => {
      push(buildCartEvent('ee_checkout'));
      return true;
    };
    if (cartRefresh) {
      const waitForCart = async () => {
        while (initialized && generation === activeGeneration) {
          const pending = cartRefresh;
          if (!pending) return cartReady;
          try {
            await pending;
          } catch {
            return false;
          }
        }
        return false;
      };
      waitForCart().then((ready) => {
        if (ready && cartReady && initialized && generation === activeGeneration) trackCheckout();
      });
    } else if (cartReady) trackCheckout();
  }

  function initCartUpdates() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      const activeGeneration = generation;
      cartUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        try {
          if (!initialized || generation !== activeGeneration) return;
          if (cartRehydration) {
            cartRehydrationEvents.push(event);
            return;
          }
          handleCartUpdate(event, activeGeneration);
        } catch (error) {
          console.warn('[GTM compatibility] Cart event failed', error);
        }
      });
    }
  }

  function initKlaviyo() {
    windowRef.addEventListener?.('klaviyoForms', (event) => {
      if (event.detail?.type !== 'submit') return;
      push({
        event: 'klaviyo_submit',
        formId: event.detail.formId,
        formTitle: event.detail.metaData?.$source ?? null,
      });
    });
  }

  function canLoadGTM() {
    try {
      const privacy = windowRef.Shopify?.customerPrivacy;
      return Boolean(
        privacy?.preferencesProcessingAllowed?.() &&
          privacy?.analyticsProcessingAllowed?.() &&
          privacy?.marketingAllowed?.(),
      );
    } catch (error) {
      console.warn('[GTM compatibility] Privacy API error', error);
      return false;
    }
  }

  function loadGTM() {
    if (!initialized || gtmLoaded || !gtmId || !canLoadGTM()) return;
    if (documentRef.querySelector('script[data-gtm-compatibility="true"]')) {
      gtmLoaded = true;
      return;
    }
    windowRef.dataLayer = windowRef.dataLayer || [];
    windowRef.dataLayer.push({'gtm.start': Date.now(), event: 'gtm.js'});
    const firstScript = documentRef.getElementsByTagName('script')[0];
    const script = documentRef.createElement('script');
    script.async = true;
    script.dataset.gtmCompatibility = 'true';
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
    firstScript.parentNode.insertBefore(script, firstScript);
    gtmLoaded = true;
  }

  function initConsent() {
    const activeGeneration = generation;
    const onConsent = () => {
      if (initialized && generation === activeGeneration) loadGTM();
    };
    documentRef.addEventListener('visitorConsentCollected', onConsent);
    removers.push(() => documentRef.removeEventListener('visitorConsentCollected', onConsent));
    if (!windowRef.Shopify?.loadFeatures) return;
    try {
      windowRef.Shopify.loadFeatures([{name: 'consent-tracking-api', version: '0.1'}], (error) => {
        if (error) console.warn('[GTM compatibility] Consent API error', error);
        onConsent();
      });
    } catch (error) {
      console.warn('[GTM compatibility] Consent API error', error);
    }
  }

  function init() {
    if (initialized) return;
    initialized = true;
    generation += 1;
    const activeGeneration = generation;
    windowRef.dataLayer = windowRef.dataLayer || [];
    pushPageEvent();
    if (hasInitialized) rehydrateCart(activeGeneration);
    hasInitialized = true;
    initProductClicks();
    initCartUpdates();
    if (!gtmId) {
      if (!invalidGtmIdWarned) {
        invalidGtmIdWarned = true;
        console.warn('[GTM compatibility] Missing custom.gtm_container_id');
      }
      return;
    }
    initConsent();
  }

  function destroy() {
    initialized = false;
    generation += 1;
    cartReady = false;
    cartRefresh = null;
    cartRefreshRequested = false;
    cartRehydration = null;
    cartRehydrationEvents = [];
    lastCheckoutAt = -Infinity;
    removers.splice(0).forEach((remove) => remove());
    cartUnsubscribe?.();
    cartUnsubscribe = null;
  }

  windowRef.GTMTrackingCompatibility = {init, loadGTM, buildLineEvent, diffCarts, pushPageEvent, destroy};
  initKlaviyo();
  init();
})(window, document);
